import { BLOCKED, STAGES, stageLabel } from '@/utils/labels';

/** A small warning sign, in the danger colour: the blocked tag. */
const BlockedSign = () => (
  <svg viewBox="0 0 16 16" className="h-3.5 w-3.5 shrink-0 text-danger" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M8 2.2l6.2 11H1.8z" />
    <path d="M8 6.6v3M8 11.6v.1" />
  </svg>
);

/**
 * A lead's stage: a five-step meter (New, Clarifications, Configuring, Paid, Live). The
 * meter's position carries the meaning, so it reads without color; filled steps use the
 * viz accent, the rest a faint track (dataviz meter: same hue, lighter track).
 */
export const StageMeter = ({ stage }: { stage: string }) => {
  const at = STAGES.findIndex((s) => s.value === stage);
  return (
    <span className="inline-flex items-center gap-0.5" aria-hidden>
      {STAGES.map((s, i) => (
        <span key={s.value} className={`h-2.5 w-1.5 rounded-sm ${i <= at ? 'bg-viz-accent' : 'bg-viz-accent opacity-20'}`} />
      ))}
    </span>
  );
};

/** The blocked tag: a warning sign and the word, so it never rests on the colour. */
export const BlockedTag = () => (
  <span className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-danger" title={BLOCKED.hint}>
    <BlockedSign />
    {BLOCKED.label}
  </span>
);

/**
 * The stage and its meter, and the blocked tag when the lead has it. With `platform`, a
 * stage a person set also says where the platform has the lead. An AI suggestion of a
 * blocker ("blockers") proposes the tag, so it shows as the tag.
 */
export const StageTag = ({ stage, platform, blocked }: { stage: string; platform?: string; blocked?: boolean }) => {
  if (stage === 'blockers') return <BlockedTag />;
  const hint = STAGES.find((s) => s.value === stage)?.hint;
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap text-sm text-fg" title={hint}>
      <StageMeter stage={stage} />
      {stageLabel(stage)}
      {platform && platform !== stage && <span className="-ml-1 text-fg-muted">· at {stageLabel(platform)}</span>}
      {blocked && <BlockedTag />}
    </span>
  );
};
